if (!customElements.get('infinity-slider')) {
  class InfinitySlider extends HTMLElement {
    constructor() {
      super();

      // If slider is marked as mobile-only, initialize only on mobile screens
      const isMobile = window.innerWidth < 750 || screen.width < 750;
      const onlyMobile = this.hasAttribute('data-only-mobile');
      if (onlyMobile && !isMobile) {
        return;
      }

      this.initializeElements();
      this.initializeState();
      this.setBasePosition();
      this.updateDots();
      this.addEventListeners();
      if (this.hasAttribute('data-move')) {
        this.setAutoMoveSlider();
      }
    }

    // Initializes DOM elements and calculates slide width
    initializeElements() {
      this.track = this.querySelector('[data-slider-track]');
      this.swipeArea = this.querySelector('[data-swipe]');
      this.slides = this.track.querySelectorAll('[data-slide]');
      this.cloneTrack = this.track.cloneNode(true); // Clone track for infinite effect
      // Since slides are 100% width, use container width
      this.slideWidth = this.swipeArea.getBoundingClientRect().width;

      // Take into account CSS gap between slides (e.g. gap: 10px on track)
      const trackStyles = window.getComputedStyle(this.track);
      const gapValue = parseInt(trackStyles.columnGap || trackStyles.gap || '0', 10) || 0;
      this.slideStep = this.slideWidth + gapValue;

      this.maxIndex = this.slides.length - 1;
      this.dots = this.querySelectorAll('[data-dot]');
    }

    // Initializes the slider's state
    initializeState() {
      this.currentSlide = 0;
      this.currentIndex = 0;
      this.swipeStartX = 0;
      this.autoMoveInterval = null;
      this.state = this.hasAttribute('data-move') || false;
      this.isSwiping = false;
      this.hasSwiped = false;
      this.desktopSwipeHandlers = {};
      this.desktopSwipeEnabled = false;
    }

    // Setup events listeners
    addEventListeners() {
      // Check if swipe area exists
      if (this.swipeArea) {
        // Initialize swipe event listeners for mobile
        this.handleSwipeMobile();
        // Initialize swipe event listeners for desktop
        this.handleSwipeDesktop();
      }
    }

    handleSwipeMobile() {
      this.swipeArea.addEventListener('touchstart', (e) => {
        this.state = false;
        this.swipeStartX = Math.round(e.touches[0].clientX);
        this.isSwiping = true;
        this.hasSwiped = false;
      }, { passive: false });

      this.swipeArea.addEventListener('touchmove', (e) => {
        if (!this.isSwiping) return;
        // Prevent default scrolling behavior during swipe
        e.preventDefault();
        this.swipeMoveX = Math.round(e.touches[0].clientX);
        // Mark as swiped if movement exceeds threshold
        const swipeDistance = Math.abs(this.swipeStartX - this.swipeMoveX);
        if (swipeDistance > 30) {
          this.hasSwiped = true;
        }
      }, { passive: false });

      this.swipeArea.addEventListener('touchend', (e) => {
        if (!this.isSwiping) return;
        this.isSwiping = false;
        let swipeEndX = Math.round(e.changedTouches[0].clientX);
        let swipeDistance = this.swipeStartX - swipeEndX;

        // Define a threshold to prevent accidental swipes
        const swipeThreshold = 30;
        if (Math.abs(swipeDistance) > swipeThreshold) {
          // Swipe occurred
          if (swipeDistance > swipeThreshold) {
            this.moveToSlide('next');
          } else if (swipeDistance < -swipeThreshold) {
            this.moveToSlide('prev');
          }
        } else if (!this.hasSwiped) {
          // No swipe occurred (no significant movement), check if we should navigate to link
          this.handleClickNavigation(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
        }
        // Reset hasSwiped for next interaction
        this.hasSwiped = false;
      });

      this.swipeArea.addEventListener('touchcancel', () => {
        this.isSwiping = false;
        this.hasSwiped = false;
      });
    }

    handleSwipeDesktop() {
      // Only enable desktop swipe on screens >= 990px
      const mediaQuery = window.matchMedia('(min-width: 990px)');

      // Create bound event handlers
      this.desktopSwipeHandlers.mousedown = (e) => {
        this.state = false;
        this.swipeStartX = Math.round(e.clientX);
        this.isSwiping = true;
        this.hasSwiped = false;
        e.preventDefault();
      };

      this.desktopSwipeHandlers.mousemove = (e) => {
        if (!this.isSwiping) return;
        e.preventDefault();
        this.swipeMoveX = Math.round(e.clientX);
        // Mark as swiped if movement exceeds threshold
        const swipeDistance = Math.abs(this.swipeStartX - this.swipeMoveX);
        if (swipeDistance > 30) {
          this.hasSwiped = true;
        }
      };

      this.desktopSwipeHandlers.mouseup = (e) => {
        if (!this.isSwiping) return;
        this.isSwiping = false;
        let swipeEndX = Math.round(e.clientX);
        let swipeDistance = this.swipeStartX - swipeEndX;

        // Define a threshold to prevent accidental swipes
        const swipeThreshold = 30;
        if (Math.abs(swipeDistance) > swipeThreshold) {
          // Swipe occurred
          if (swipeDistance > swipeThreshold) {
            this.moveToSlide('next');
          } else if (swipeDistance < -swipeThreshold) {
            this.moveToSlide('prev');
          }
        } else if (!this.hasSwiped) {
          // No swipe occurred (no significant movement), check if we should navigate to link
          this.handleClickNavigation(e.clientX, e.clientY);
        }
        // Reset hasSwiped for next interaction
        this.hasSwiped = false;
      };

      this.desktopSwipeHandlers.mouseleave = (e) => {
        this.isSwiping = false;
        this.hasSwiped = false;
      };

      const enableDesktopSwipe = () => {
        if (mediaQuery.matches && !this.desktopSwipeEnabled) {
          this.swipeArea.addEventListener('mousedown', this.desktopSwipeHandlers.mousedown);
          this.swipeArea.addEventListener('mousemove', this.desktopSwipeHandlers.mousemove);
          this.swipeArea.addEventListener('mouseup', this.desktopSwipeHandlers.mouseup);
          this.swipeArea.addEventListener('mouseleave', this.desktopSwipeHandlers.mouseleave);
          this.desktopSwipeEnabled = true;
        } else if (!mediaQuery.matches && this.desktopSwipeEnabled) {
          this.swipeArea.removeEventListener('mousedown', this.desktopSwipeHandlers.mousedown);
          this.swipeArea.removeEventListener('mousemove', this.desktopSwipeHandlers.mousemove);
          this.swipeArea.removeEventListener('mouseup', this.desktopSwipeHandlers.mouseup);
          this.swipeArea.removeEventListener('mouseleave', this.desktopSwipeHandlers.mouseleave);
          this.desktopSwipeEnabled = false;
        }
      };

      // Enable on initial load if desktop
      enableDesktopSwipe();

      // Re-enable/disable when window is resized
      mediaQuery.addEventListener('change', enableDesktopSwipe);
    }

    // Handles slide navigation and sets up the infinite effect
    moveToSlide(direction) {
      this.state = false;
      if (direction === 'next') {
        this.currentSlide++;
        this.handleMoveSlider(direction);
        this.currentIndex++;
        this.currentIndex = this.setupInfinityLoop(this.currentIndex);
      } else {
        this.currentSlide--;
        this.currentIndex--;
        this.currentIndex = this.setupInfinityLoop(this.currentIndex);
        this.handleMoveSlider(direction);
      }
      this.updateDots();
    }

    // Moves the slider to the current slide position
    handleMoveSlider(direction) {
      this.track.style.transform = `translateX(${-this.currentSlide * this.slideStep}px)`;
      this.updateItems(direction);
    }

    // Clones and appends/prepends items to create infinite scrolling effect
    updateItems(direction) {
      const cloneSlide = [...this.cloneTrack.children][this.currentIndex].cloneNode(true);
      if (direction === 'next') {
        this.appendCloneSlide(cloneSlide);
      } else {
        this.prependCloneSlide(cloneSlide);
      }
    }

    // Appends a cloned slide and removes the first element
    appendCloneSlide(cloneSlide) {
      cloneSlide.style.transform = `translateX(${((this.maxIndex + this.currentSlide) * this.slideStep)}px)`;
      this.track.append(cloneSlide);
      const transitionSpeed = parseInt(this.dataset.transitionSpeed, 10) || 400;
      setTimeout(() => this.track.firstElementChild.remove(), transitionSpeed);
    }

    // Prepends a cloned slide and removes the last element
    prependCloneSlide(cloneSlide) {
      cloneSlide.style.transform = `translateX(${this.currentSlide * this.slideStep}px)`;
      this.track.prepend(cloneSlide);
      const transitionSpeed = parseInt(this.dataset.transitionSpeed, 10) || 400;
      setTimeout(() => this.track.lastElementChild.remove(), transitionSpeed);
    }

    // Positions each slide based on index for initial setup
    setBasePosition() {
      let translate = 0;
      [...this.track.children].forEach((elem) => {
        elem.style.transform = `translateX(${translate}px)`;
        translate += this.slideStep;
      });
    }

    // Resets currentIndex within bounds for infinite loop effect
    setupInfinityLoop(currentIndex) {
      return currentIndex = (currentIndex + this.maxIndex + 1) % (this.maxIndex + 1);
    }

    // Updates active dot state
    updateDots() {
      if (this.dots.length > 0) {
        this.dots.forEach((dot, index) => {
          if (index === this.currentIndex) {
            dot.setAttribute('data-active', '');
          } else {
            dot.removeAttribute('data-active');
          }
        });
      }
    }

    setAutoMoveSlider() {
      if (this.autoMoveInterval) {
        clearInterval(this.autoMoveInterval);
      }

      this.autoMoveInterval = setInterval(() => {
        if (this.state) {
          this.currentSlide++;
          this.handleMoveSlider('next');
          this.currentIndex++;
          this.currentIndex = this.setupInfinityLoop(this.currentIndex);
          this.updateDots();
        } else {
          clearInterval(this.autoMoveInterval);
        }
      }, parseInt(this.dataset.duration, 10) * 1000);
    }

    // Handles click navigation to main link if no swipe occurred
    handleClickNavigation(x, y) {
      // Check if there's a main link
      const mainLink = this.swipeArea?.dataset.mainLink;
      if (!mainLink) return;

      // Get the element at the click position
      const elementAtPoint = document.elementFromPoint(x, y);

      // Check if the click was on a link or inside a link
      const clickedLink = elementAtPoint?.closest('a');
      if (clickedLink) {
        // User clicked on a link, let it handle navigation
        return;
      }

      // Check if click was on dots
      const clickedDot = elementAtPoint?.closest('[data-dot]');
      if (clickedDot) {
        // User clicked on a dot, don't navigate
        return;
      }

      // No swipe occurred and not clicking on a link or dot, navigate to main link
      window.location.href = mainLink;
    }
  }

  customElements.define('infinity-slider', InfinitySlider);
}

if (!customElements.get('comparative-slider')) {
  class ComparativeSlider extends HTMLElement {
    constructor() {
      super();
      // Query selectors for necessary elements
      this.cardText = this.querySelectorAll('.comparative-slider__txt');
      this.beforeElement = this;
      this.afterElement = this.querySelector('[data-after]');
      this.divider = this.querySelector('.comparative-slider__divider');
      this.isDragging = false;

      // Event listeners for dragging functionality (Mouse and Touch) on the divider
      this.divider.addEventListener('mousedown', this.startDrag.bind(this));
      this.divider.addEventListener('mousemove', this.onDrag.bind(this));
      this.divider.addEventListener('mouseup', this.stopDrag.bind(this));

      // Touch events for mobile support on the divider
      this.divider.addEventListener('touchstart', this.startDrag.bind(this));
      this.divider.addEventListener('touchmove', this.onDrag.bind(this));
      this.divider.addEventListener('touchend', this.stopDrag.bind(this));

      // Initial setup
      this.compareElements();
    }

    // Initial setup of the divider position
    compareElements() {
      this.updateDivider(50); // Center the divider at 50%
    }

    // Starts the dragging process (Handles both mouse and touch)
    startDrag(e) {
      e.stopPropagation();
      this.isDragging = true;
      this.onDrag(e); // Update immediately on mousedown/touchstart
    }

    // Handles the dragging movement (Supports both mouse and touch)
    onDrag(e) {
      e.stopPropagation();
      if (this.isDragging) {
        let clientX;

        // Determine the clientX position based on mouse or touch event
        if (e.type === 'mousemove') {
          clientX = e.clientX;
        } else if (e.type === 'touchmove') {
          clientX = e.touches[0].clientX;
        }

        if (clientX) {
          // Get the bounding rectangle of the before element
          const rect = this.beforeElement.getBoundingClientRect();
          // Calculate the mouse/touch position relative to the element
          const positionX = clientX - rect.left;
          const width = rect.width;
          // Calculate the percentage based on the position
          const percentage = (positionX / width) * 100;

          // Limit the percentage to stay between 0% and 100%
          const clampedPercentage = Math.max(0, Math.min(percentage, 100));

          // Update the clip-path of the after element based on the percentage
          this.afterElement.style.clipPath = `inset(0 ${100 - clampedPercentage}% 0 0)`;
          // Update the divider position based on the percentage
          this.updateDivider(clampedPercentage);
          // Check and update the visibility of text elements
          this.checkTextVisibility(clampedPercentage);
        }
      }
    }

    // Stops the dragging process (Handles both mouse and touch)
    stopDrag(e) {
      e.stopPropagation();
      this.isDragging = false;
    }

    // Updates the position of the divider
    updateDivider(percentage) {
      this.divider.style.left = `${percentage}%`;
    }

    // Checks if the divider overlaps any text elements and updates their data-active attribute
    checkTextVisibility(percentage) {
      // Calculate the position of the divider based on the percentage
      const dividerPosition = (percentage / 100) * this.beforeElement.clientWidth;

      this.cardText.forEach(textElement => {
        // Get the bounding rectangle of the text element and the before element
        const textRect = textElement.getBoundingClientRect();
        const elementRect = this.beforeElement.getBoundingClientRect();

        // Check if the divider overlaps the text element
        const isOverlapping =
          (textRect.left < elementRect.left + dividerPosition &&
            textRect.right > elementRect.left + dividerPosition);

        // Update the data-active attribute based on overlap
        textElement.setAttribute('data-active', !isOverlapping);
      });
    }
  }

  // Define the custom element
  customElements.define('comparative-slider', ComparativeSlider);
}
